import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import {
    Box,
    Button,
    cssVar,
    Divider,
    EmptyState,
    Flex,
    Grid,
    Tag,
    Text,
    VStack,
} from '@cad-arc';
import { Panel } from '../panels/panel';
import { RESIZE_DISABLED } from '../panels/panel-configuration';
import { SensorEventWorkspacePanel } from './client-only-panel-types';
import {
    findSensorAlertIndex,
    formatSensorAlertClipTime,
    formatSensorAlertTime,
    getSensorAlertConfidence,
    getSensorAlertDurationSeconds,
    getSensorAlertEndUtc,
    getSensorAlertObjectType,
    getSensorAlertTags,
} from './helpers';
import { useEntityPanelManager } from '../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { AttachToEventButton } from './attach-to-event-button';
import { getLinkedEventIds } from './use-link-sensor-alerts';
import { useArchiveSensorAlert } from './use-archive-sensor-alert';
import { useCreateEventFromAlert } from './use-create-event-from-alert';
import { useOpenSensorPanel } from './use-open-sensor-panel';
import { useSensorDispatchAreaName } from './use-sensor-dispatch-area-name';
import { useSensorPriority } from './use-sensor-priority';
import { useSensorsStore } from './use-sensors-store';

/** How many tags get the highlighted treatment, per the design's amber Motion / Person pair. */
const HIGHLIGHTED_TAG_COUNT = 2;

/**
 * Every clip renders at this height regardless of the source's aspect ratio.
 *
 * Load-bearing: the vendor's images are not one shape — a motion GIF arrives roughly 16:9 while a
 * person-of-interest crop is close to square — and an uncapped `width: 100%` turns the tall ones
 * into something several panel-heights tall, which also puts a scrollbar down the side of the clip.
 * `contain` rather than `cover` because cropping a detection frame can hide the thing detected.
 */
const CLIP_HEIGHT = 240;

function MetadataItem({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <VStack align="stretch" spacing={0} minWidth={0}>
            <Text variant="caption" color="secondary" fontSize="10px" casing="uppercase">
                {label}
            </Text>
            {children}
        </VStack>
    );
}

function MetadataValue({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <Text variant="bodyMd" fontFamily="mono" fontWeight="bold">
            {children}
        </Text>
    );
}

function EventTag({
    children,
    isHighlighted = false,
}: {
    children: React.ReactNode;
    isHighlighted?: boolean;
}): React.ReactElement {
    // Reassign the custom properties `StyledTag` defines rather than rebuilding the pill, so radius
    // and metrics stay Arc's.
    const highlightStyle = {
        '--arc-tag-default-bg': cssVar('arc.colors.warning.accent'),
        '--arc-tag-default-text': cssVar('arc.colors.warning.accentContent'),
    } as React.CSSProperties;

    return <Tag style={isHighlighted ? highlightStyle : undefined}>{children}</Tag>;
}

/**
 * Prefixed with the panel's own type, so a stack of sensor and sensor event panels over the map is
 * readable without reading their contents.
 */
function getSensorEventTitle(alert: SensorAlertBundle | undefined): string {
    const subject = alert?.alertName ?? (alert ? getSensorAlertObjectType(alert) : undefined);
    return ['Sensor Event', subject].filter(Boolean).join(' · ');
}

const SensorEventPanelHeader = observer(function SensorEventPanelHeader({
    alertKey,
}: {
    alertKey: string | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const alerts = sensorsStore.alerts;
    const alertIndex = findSensorAlertIndex(alerts, alertKey);

    return (
        // `PanelHeader` already pads; a second inset would misalign this against the close button.
        <Flex flexGrow={1} minWidth={0} alignItems="center" height="2rem">
            <Text variant="headingXs" noOfLines={1}>
                {getSensorEventTitle(alertIndex === -1 ? undefined : alerts[alertIndex])}
            </Text>
        </Flex>
    );
});

/**
 * The clip. `imageUrl` is an animated GIF of the triggering moment, and since the convergence
 * branch dropped `videoUrl` from the alert it is now the only footage an alert carries — so there
 * is no playback position to drive a seek bar, and the design's scrubber, play button and
 * `00:03 / 00:08` readout are deliberately absent rather than rendered dead. Live footage still
 * has a real player; that comes from the stream token, not from the alert. See `camera-video.tsx`.
 */
function SensorEventClip({ alert }: { alert: SensorAlertBundle }): React.ReactElement {
    const endTime = formatSensorAlertClipTime(getSensorAlertEndUtc(alert));
    const clipWindow = [
        alert.createdDateUtc ? formatSensorAlertTime(alert.createdDateUtc) : undefined,
        endTime,
    ]
        .filter(Boolean)
        .join(' → ');

    const clipStyle: React.CSSProperties = {
        width: '100%',
        height: `${CLIP_HEIGHT}px`,
        objectFit: 'contain',
        display: 'block',
    };

    return (
        <Box position="relative" backgroundColor="surface.canvas" flexShrink={0}>
            {alert.imageUrl ? (
                <img src={alert.imageUrl} alt="Sensor event clip" style={clipStyle} />
            ) : (
                <Flex
                    height={`${CLIP_HEIGHT}px`}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        backgroundImage:
                            'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)',
                    }}
                >
                    <Text variant="caption" color="secondary" fontFamily="mono">
                        EVENT CLIP
                    </Text>
                </Flex>
            )}
            {clipWindow ? (
                <Box position="absolute" top={2} left={2} pointerEvents="none">
                    <Flex
                        height="22px"
                        alignItems="center"
                        paddingX={1}
                        borderRadius="sm"
                        backgroundColor="surface.foreground"
                    >
                        <Text variant="caption" fontFamily="mono">
                            {clipWindow}
                        </Text>
                    </Flex>
                </Box>
            ) : undefined}
        </Box>
    );
}

const SensorEventMetadata = observer(function SensorEventMetadata({
    alert,
}: {
    alert: SensorAlertBundle;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const entityPanelManager = useEntityPanelManager();
    const sensor = sensorsStore.getSensorForAlert(alert);
    const priority = useSensorPriority(alert);
    const dispatchAreaName = useSensorDispatchAreaName(sensor);

    const linkedEventIds = getLinkedEventIds(alert);
    /*
     * Event numbers are only resolvable for events open as panels — the alert carries ids, and
     * there is no bulk id-to-number lookup. An id is a poor label but an honest one.
     */
    const eventNumbersById = new Map(
        entityPanelManager.eventPanelsList
            .filter((eventPanel) => eventPanel.eventId !== undefined)
            .map((eventPanel) => [
                eventPanel.eventId as number,
                eventPanel.metadata?.agencyEventNumber,
            ])
    );

    const durationSeconds = getSensorAlertDurationSeconds(alert);
    const confidence = getSensorAlertConfidence(alert);
    const tags = [
        ...getSensorAlertTags(alert),
        ...(confidence === undefined ? [] : [`Confidence ${confidence.toFixed(2)}`]),
    ];

    return (
        <VStack align="stretch" spacing={3} padding={3}>
            <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                {/* The vendor's own camera id is on the sensor panel; repeating it here just
                    wrapped onto three lines and pushed the layout around. */}
                <MetadataItem label="Sensor">
                    <MetadataValue>{sensor?.name ?? '–'}</MetadataValue>
                </MetadataItem>
                <MetadataItem label="Detected">
                    <MetadataValue>
                        {[
                            formatSensorAlertClipTime(alert.createdDateUtc) ?? '–',
                            durationSeconds === undefined ? undefined : `${durationSeconds} s`,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </MetadataValue>
                </MetadataItem>
            </Grid>
            <MetadataItem label="Site">
                <MetadataValue>
                    {[sensor?.site, sensor?.address].filter(Boolean).join(' · ') || '–'}
                </MetadataValue>
            </MetadataItem>
            {/* Mapped per alert type on the sensors support page and frozen onto the alert when
                it arrived; absent for a type nobody has mapped. */}
            {priority.label ? (
                <MetadataItem label="Priority">
                    <MetadataValue>{priority.label}</MetadataValue>
                </MetadataItem>
            ) : undefined}
            {/* The camera's current classification. The alert used to carry its own, resolved at
                the moment it was received, which was the one to trust — a camera reclassified
                afterwards now retroactively changes what an old alert appears to say. Dropped
                from the alert on the convergence branch, so this is the only source left. */}
            {dispatchAreaName ? (
                <MetadataItem label="D-Area">
                    <MetadataValue>{dispatchAreaName}</MetadataValue>
                </MetadataItem>
            ) : undefined}
            {/* What this alert is attached to. Learned from the alert itself — link and unlink
                both push the whole updated alert, so `linkedEvents` is the only signal, and an
                unlink is the eventId simply no longer being here. */}
            {linkedEventIds.length > 0 ? (
                <MetadataItem label="Linked CAD events">
                    <MetadataValue>
                        {linkedEventIds
                            .map((eventId) => eventNumbersById.get(eventId) ?? `Event ${eventId}`)
                            .join(' · ')}
                    </MetadataValue>
                </MetadataItem>
            ) : undefined}
            <MetadataItem label="Tags">
                <Flex gap={1} flexWrap="wrap" paddingTop={1}>
                    {tags.map((tag, index) => (
                        <EventTag key={tag} isHighlighted={index < HIGHLIGHTED_TAG_COUNT}>
                            {tag}
                        </EventTag>
                    ))}
                </Flex>
            </MetadataItem>
        </VStack>
    );
});

/**
 * Create event opens a new event that links this one when it is saved (see
 * `useCreateEventFromAlert`); Attach to event links it to an event that already exists.
 */
const SensorEventActions = observer(function SensorEventActions({
    alert,
}: {
    alert: SensorAlertBundle;
}): React.ReactElement {
    const openSensorPanel = useOpenSensorPanel();
    const archiveSensorAlert = useArchiveSensorAlert();
    const createEventFromAlert = useCreateEventFromAlert();
    const sensorsStore = useSensorsStore();
    const isArchived = sensorsStore.isAlertArchived(alert);

    return (
        <Flex gap={2} padding={3} alignItems="center" flexWrap="wrap">
            <Button variant="solid" onClick={() => createEventFromAlert(alert)}>
                Create event
            </Button>
            <AttachToEventButton alert={alert} />
            <Flex marginLeft="auto" gap={2} alignItems="center">
                <Button
                    variant="ghost"
                    trailingVisual="OpenNewWindow"
                    isDisabled={!alert.sensorExternalId}
                    onClick={() => openSensorPanel({ sensorId: alert.sensorExternalId })}
                >
                    Open Sensor Panel
                </Button>
                <Button
                    variant="ghost"
                    leadingVisual="Archive"
                    onClick={() => archiveSensorAlert(alert, !isArchived)}
                >
                    {isArchived ? 'Restore' : 'Archive'}
                </Button>
            </Flex>
        </Flex>
    );
});

const SensorEventPanelBody = observer(function SensorEventPanelBody({
    alertKey,
}: {
    alertKey: string | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const alerts = sensorsStore.alerts;
    const alertIndex = findSensorAlertIndex(alerts, alertKey);
    const alert = alertIndex === -1 ? undefined : alerts[alertIndex];

    if (!alert) {
        return (
            <VStack align="stretch" padding={2} flexGrow={1} overflow="auto">
                {/* The log is capped, so an old event can age out from under an open panel. */}
                <EmptyState
                    visual="Camera"
                    title="Sensor event unavailable"
                    subtitle="This event has aged out of the recent sensor event history."
                />
            </VStack>
        );
    }

    return (
        /*
         * Two regions rather than one scrolling column: the content scrolls and the action footer
         * stays pinned to the bottom edge whatever the panel's height. `PanelContainer` is already
         * a flex column, so `height: 100%` here is what the footer anchors against.
         */
        <Flex
            direction="column"
            height="100%"
            minHeight={0}
            backgroundColor="surface.canvas"
            overflow="hidden"
        >
            <Box flexGrow={1} minHeight={0} overflowY="auto">
                <SensorEventClip alert={alert} />
                <SensorEventMetadata alert={alert} />
            </Box>
            <Box flexShrink={0}>
                <Divider />
                <SensorEventActions alert={alert} />
            </Box>
        </Flex>
    );
});

export function SensorEventPanel({
    panel,
}: {
    panel: SensorEventWorkspacePanel;
}): React.ReactElement {
    const alertKey = panel.getPanelState('alertKey');

    return (
        <Panel
            panel={panel}
            testId="SENSOR_EVENT_PANEL"
            // Laid out for one size: a fixed-height clip, a three-column grid and a pinned footer.
            resizeOptions={RESIZE_DISABLED}
            header={<SensorEventPanelHeader alertKey={alertKey} />}
            body={<SensorEventPanelBody alertKey={alertKey} />}
        />
    );
}
